import { useState, useEffect, forwardRef } from "react"
import {
  Navigation,
  Pagination,
  Mousewheel,
  Keyboard,
  Controller,
  Autoplay,
} from "swiper"
import { Swiper, SwiperSlide, useSwiper } from "swiper/react"
import classNames from "classnames"
import { mergeClasses } from "@lib/css"
import { IconArrowLeft, IconArrowRight } from "@lib/icons"

import type { ReactElement, ButtonHTMLAttributes } from "react"
import type { SwiperOptions } from "swiper"

type SliderProps = {
  children: ReactElement<typeof SwiperSlide>[]
  navigation?: {
    ArrowLeftIcon?: JSX.Element
    ArrowRightIcon?: JSX.Element
  }
  className?: string
  options?: SwiperOptions
  classes?: {
    container?: string
    navigation?: {
      container?: string
      prev?: string
      next?: string
    }
  }
}

export function Slider(props: SliderProps) {
  const { children, className, classes, options, navigation } = props
  const [swiper, setSwiper] = useState<ReturnType<typeof useSwiper> | null>(
    null,
  )
  const [navigationDisabled, setNavigationDisabled] = useState({
    prev: true,
    next: false,
  })

  useEffect(() => {
    let timeout
    if (swiper !== null) {
      timeout = setTimeout(() => {
        swiper.update()
      }, 1000)

      swiper.on("slideChange", (swiperInstance) => {
        if (!options?.loop) {
          setNavigationDisabled({
            prev: swiperInstance.isBeginning,
            next: swiperInstance.isEnd,
          })
        }
      })
    }

    return function cleanup() {
      if (swiper !== null) {
        clearTimeout(timeout)
        swiper.destroy()
      }
    }
  }, [swiper, options?.loop])

  function goNext() {
    if (swiper !== null) {
      swiper.slideNext()
    }
  }

  function goPrev() {
    if (swiper !== null) {
      swiper.slidePrev()
    }
  }

  // @ts-ignore
  return (
    <div className={classNames("relative", classes?.container)}>
      <Swiper
        speed={180}
        observer={true}
        modules={[
          Navigation,
          Pagination,
          Mousewheel,
          Keyboard,
          Controller,
          Autoplay,
        ]}
        pagination={{
          el: ".swiper-pagination",
        }}
        onSwiper={(swiper) => setSwiper(swiper)}
        className={className}
        {...options}
      >
        {children}
      </Swiper>
      {options?.navigation ? (
        <div
          className={classNames(
            "hidden laptop:flex laptop:items-center laptop:justify-between",
            "absolute -inset-x-10 top-1/2 transform -translate-y-1/2 laptop:-inset-x-14",
            classes?.navigation?.container,
          )}
        >
          <NavigationHandler
            className={classes?.navigation?.prev}
            icon={
              navigation?.ArrowLeftIcon || (
                <IconArrowLeft className="w-full h-full" />
              )
            }
            onClick={goPrev}
            disabled={!options.loop && navigationDisabled.prev}
            data-test-id="slider__prev_button"
          />
          <NavigationHandler
            className={classes?.navigation?.next}
            icon={
              navigation?.ArrowRightIcon || (
                <IconArrowRight className="w-full h-full" />
              )
            }
            onClick={goNext}
            disabled={!options.loop && navigationDisabled.next}
            data-test-id="slider__next_button"
          />
        </div>
      ) : null}
    </div>
  )
}

type NavigationHandlerProps = {
  icon: ReactElement<typeof IconArrowRight | typeof IconArrowLeft>
} & ButtonHTMLAttributes<HTMLButtonElement>

const NavigationHandler = forwardRef<HTMLButtonElement, NavigationHandlerProps>(
  function NavigationHandler(props, ref) {
    const { className, icon, ...otherProps } = props

    const defaultClasses =
      "w-10 h-10 flex items-center justify-center text-inherit cursor-pointer opacity-50 border-none bg-transparent transition-opacity duration-100 ease-in hover:opacity-100 disabled:cursor-default disabled:opacity-20"
    const classes = mergeClasses(defaultClasses, className)

    return (
      <button
        ref={ref}
        className={classes}
        type="button"
        aria-label="Next slide"
        {...otherProps}
      >
        {icon}
      </button>
    )
  },
)

export { SwiperSlide as SliderSlide }
